import { STORE, del, get, getAll, put } from './db';
import type { SyncItemKind } from './syncPlanner';

/**
 * 同期で衝突したセーブを保留しておくストア。
 *
 * 自動でどちらかを消すことは絶対にしない。
 * 「30 分遊んだセーブが黙って消える」のはこのアプリで最悪の不具合なので、
 * 判断はユーザーに委ね、それまで両方を保持する。
 */
export interface ConflictRecord {
  /** `${romId}:${kind}:${slot}` */
  id: string;
  romId: string;
  romTitle: string;
  kind: SyncItemKind;
  slot: number;

  localUpdatedAt: number;
  localSize: number;
  /** ステートの場合のサムネイル */
  localThumbnail?: string;

  remoteUpdatedAt: number;
  remoteSize: number;
  remoteEtag: string;
  /** 取得済みのリモート本体。解決時に再ダウンロードしなくて済む。 */
  remoteData: ArrayBuffer;

  detectedAt: number;
}

export function conflictId(romId: string, kind: SyncItemKind, slot: number): string {
  return `${romId}:${kind}:${slot}`;
}

export async function recordConflict(record: Omit<ConflictRecord, 'detectedAt'>): Promise<void> {
  const existing = await get<ConflictRecord>(STORE.conflicts, record.id);
  await put(STORE.conflicts, {
    ...record,
    // 既知の競合なら検出時刻は最初のものを保つ（UI が並び替えで踊らないように）
    detectedAt: existing?.detectedAt ?? Date.now(),
  } satisfies ConflictRecord);
}

export async function listConflicts(): Promise<ConflictRecord[]> {
  const all = await getAll<ConflictRecord>(STORE.conflicts);
  return all.sort((a, b) => b.detectedAt - a.detectedAt);
}

export async function countConflicts(): Promise<number> {
  return (await getAll<ConflictRecord>(STORE.conflicts)).length;
}

export async function getConflict(id: string): Promise<ConflictRecord | undefined> {
  return get<ConflictRecord>(STORE.conflicts, id);
}

export async function clearConflict(id: string): Promise<void> {
  await del(STORE.conflicts, id);
}
